import { useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { useCountUp } from 'react-countup'
import { FormattedMessage } from 'react-intl'
import { Waypoint } from 'react-waypoint'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

// Constants
import {
  DIYUpdatePayments,
  ScrolledIntoView,
  EVENTS_LABELS,
} from '../../../../constants/trackingEvents'

// Components
import {
  Panel,
  Format,
  Lock,
  InlineTextLock,
  Select,
  Text,
} from '../../..'

// Helpers
import {
  dispatchEvent,
  oneTimeTagEventDispatcher,
} from '../../../../helpers/tagManager'
import { useAmplitudeDebounceSetters } from '../../../../hooks/tracking'

// Styles
import styles from './PrincipalPayments.module.scss'

const propTypes = {
  withCurrentHomeData: PropTypes.bool,
  addPayment: PropTypes.number.isRequired,
  setAddPayment: PropTypes.func.isRequired,
  addPaymentSavingsYears: PropTypes.number.isRequired,
  addPaymentSavings: PropTypes.number,
  principalOptions: PropTypes.array,
}

const scrollIntoView = oneTimeTagEventDispatcher(ScrolledIntoView, {
  eventLabel: EVENTS_LABELS.PrincipalPaymentsSection,
})

const PrincipalPayments = ({
  withCurrentHomeData,
  addPayment,
  setAddPayment,
  addPaymentSavings,
  principalOptions,
  addPaymentSavingsYears,
}) => {
  const { ampli } = useContext(AmplitudeContext)
  const [trackExtraPrincipalAdjusted] = useAmplitudeDebounceSetters(() =>
    ampli?.extraPrincipalAdjusted()
  )
  const [trackModuleViewed] = useAmplitudeDebounceSetters(() =>
    ampli?.moduleViewed({
      module_name: AmplitudeEventConstants.PAY_EXTRA_PRINCIPAL,
    })
  )
  const {
    countUp: countUpPaymentSavings,
    start: startCount,
    update: updateCount,
  } = useCountUp({
    start: 0,
    end: addPaymentSavings,
    duration: 1,
  })

  useEffect(() => {
    updateCount(addPaymentSavings)
  }, [addPaymentSavings]) // eslint-disable-line

  useEffect(() => {
    startCount()
  }, []) // eslint-disable-line

  const handleTrackModuleViewed = () => {
    scrollIntoView()
    trackModuleViewed()
  }
  return (
    <>
      <Panel.Heading className={styles.payMoreText}>
        <FormattedMessage
          id='Homeowner.interestCard.payMorePart1'
          defaultMessage='Tip: If you pay just {amount} more each month, you could save {total}'
          values={{
            amount: (
              <Select
                inline
                aria-label='monthly payment'
                value={addPayment}
                options={principalOptions}
                optionValue={Format.currency}
                className={styles.select}
                disabled={!withCurrentHomeData}
                onChange={value => {
                  trackExtraPrincipalAdjusted()
                  dispatchEvent(DIYUpdatePayments)
                  setAddPayment(parseFloat(value))
                }}
              />
            ),
            total: (
              <Lock enabled={!withCurrentHomeData}>
                <Text
                  size='xl'
                  weight='light'
                  variant={countUpPaymentSavings >= 0 ? 'success' : 'danger'}
                  className={styles.paymentSavings}
                >
                  <Format.Currency value={Math.round(countUpPaymentSavings)} />
                </Text>
              </Lock>
            ),
          }}
        />
      </Panel.Heading>
      <Text
        className={styles.interestText}
        size='s'
        align='center'
        variant='neutralCool'
        variantCount='900'
      >
        <FormattedMessage
          id='Homeowner.interestCard.payMorePart2'
          defaultMessage="in interest over the rest of your loan. You'd also be done with the loan and no longer making payments {yearsEarlier}!"
          values={{
            yearsEarlier: (
              <Text element='span' variant='success'>
                <FormattedMessage
                  id='Homeowner.interestCard.payMorePart2.yearsEarlier'
                  defaultMessage='{years} years earlier'
                  values={{
                    years: (
                      <InlineTextLock enabled={!withCurrentHomeData}>
                        {addPaymentSavingsYears}
                      </InlineTextLock>
                    ),
                  }}
                />
              </Text>
            ),
          }}
        />
      </Text>
      <Waypoint onEnter={handleTrackModuleViewed} bottomOffset='50%'>
        <div />
      </Waypoint>
    </>
  )
}

PrincipalPayments.propTypes = propTypes

export default PrincipalPayments
